import { computed, onScopeDispose, ref, watch, type ComputedRef, type Ref } from 'vue';

export const REFRESH_INDICATOR_DELAY_MS = 250;

export interface ContentLoadingState {
  showSkeleton: ComputedRef<boolean>;
  isRefreshing: ComputedRef<boolean>;
}

export function useContentLoadingState(
  loading: Ref<boolean>,
  hasData: Ref<boolean>
): ContentLoadingState {
  const hasRenderedContent = ref(!loading.value && hasData.value);
  const isRefreshSlow = ref(false);
  let refreshDelayTimer: ReturnType<typeof setTimeout> | undefined;

  function clearRefreshDelay(): void {
    if (refreshDelayTimer === undefined) return;
    clearTimeout(refreshDelayTimer);
    refreshDelayTimer = undefined;
  }

  watch([loading, hasData], ([isLoading, dataAvailable]) => {
    clearRefreshDelay();
    if (!isLoading) {
      hasRenderedContent.value = dataAvailable;
      isRefreshSlow.value = false;
      return;
    }
    if (!hasRenderedContent.value) return;
    refreshDelayTimer = setTimeout(() => {
      isRefreshSlow.value = true;
    }, REFRESH_INDICATOR_DELAY_MS);
  });

  onScopeDispose(clearRefreshDelay, true);

  return {
    showSkeleton: computed(() => loading.value && !hasRenderedContent.value),
    isRefreshing: computed(() => loading.value && hasRenderedContent.value && isRefreshSlow.value),
  };
}
